import React, { useState, useEffect, useCallback } from "react";

// Importing bootstrap components
import { Col, Row, Modal, Button, Spinner } from "react-bootstrap";

// Importing toast utilities
import { showErrorToast, showSuccessToast } from "utils/toast";

import { useSelector, useDispatch } from "react-redux";

// Importing user slices
import {
  addEmployee as addEmployeeInStore,
  updateEmployee as updateEmployeeInStore,
  deleteEmployee as deleteEmployeeInStore,
} from "state/slices/user/userSlice";

// Importing user mutations
import {
  useGetUsersQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useDeleteUserProfilePictureMutation,
} from "state/slices/user/userApiSlice";

// Importing Auth mutations
import { useRegisterMutation } from "state/slices/auth/authApiSlice";

// Importing other components slices
import { useGetDesignationsQuery } from "state/slices/designation/designationApiSlice";
import { useGetTeamsQuery } from "state/slices/team/teamApiSlice";
import { useGetShiftsQuery } from "state/slices/shift/shiftApiSlice";
import { useGetRolesQuery } from "state/slices/role/roleApiSlice";

// Importing employee form components
import EmployeeForm from "components/EmployeeForm";
import ModalButton from "components/ModalButton";

// Importing validators
import {
  valiDateOptionalFormField,
  isNull,
  parseUserData,
} from "validations";

const GeneralAccessManagement = React.memo(
  ({
    teamId,
    _ehphide,
    isAllowedToCreate,
    isAllowedToUpdate,
    isAllowedToDelete,
    EmployeesViewManagement,
  }) => {

    const dispatch = useDispatch();
    const { userInfo } = useSelector((state) => state.auth);

    const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
    const [editEmployeeData, setEditEmployeeData] = useState(null);

    // Retrieve Employee
    const { refetch } = useGetUsersQuery();

    // Retrieve Designations, Teams, Shifts, and Roles
    const { data: { designations: designationOptions } = {} } =
      useGetDesignationsQuery();
    const { data: { teams: teamOptions } = {} } = useGetTeamsQuery();
    const { data: { shifts: shiftOptions } = {} } = useGetShiftsQuery();
    const { data: { roles: roleOptions } = {} } = useGetRolesQuery();

    const handleCloseAddEmployeeModal = useCallback(() => {
      setShowAddEmployeeModal(false);
      setEditEmployeeData(null);
    }, []);

    const handleShowAddEmployeeModal = useCallback(() => {
      setShowAddEmployeeModal(true);
    }, []);

    const handleEditEmployee = useCallback(
      (employeeData) => {
        const formattedUserData = parseUserData(employeeData);

        setEditEmployeeData(formattedUserData);

        setShowAddEmployeeModal(true);
      },
      [showAddEmployeeModal]
    );

    const [createUser, { isLoading: isCreatingUser }] = useRegisterMutation();
    const [updateUser, { isLoading: isUpdatingUser }] = useUpdateUserMutation();
    const [deleteUser] = useDeleteUserMutation();
    const [deleteUserProfilePicture] = useDeleteUserProfilePictureMutation();
    
    const isSubmitting = isCreatingUser || isUpdatingUser;

    const handleSubmit = async (values) => {
      try {
        let userToastMessage = "registered";

        const formData = new FormData();

        const fieldsToCheck = [
          "name",
          "phone",
          "email",
          "employeeId",
          "designation",
          "team",
          "shift",
          "role",
          "dateOfJoining",
          "dateOfBirth",
          "address",
          "cl",
          "totalDD",
          "leaves",
          "recordingFrequency",
          "recordingMode",
          "bankName",
          "accountNumber",
          "ifscCode",
          "password",
          "profilePicture",
        ];

        fieldsToCheck.forEach((field) => {
          // Check if the field exists in both editEmployeeData and values
          if (!isNull(editEmployeeData)) {
            if (
              editEmployeeData.hasOwnProperty(field) &&
              values.hasOwnProperty(field)
            ) {
              // Check if the values are different
              if (editEmployeeData[field] !== values[field]) {
                let fieldName = field;
                if (["bankName", "accountNumber", "ifscCode"].includes(field)) {
                  fieldName = `bankDetails[${field}]`;
                }

                // If different, append the field to the formData
                formData.append(fieldName, values[field]);
              }
            } else {
              // If the field is not present in loggedUserInfo, append the field from values
              if (
                values.hasOwnProperty(field) &&
                valiDateOptionalFormField(values[field])
              ) {
                formData.append(field, values[field]);
              }
            }
          } else {
            // If the field is not present in editEmployeeData, append the field from values
            if (
              values.hasOwnProperty(field) &&
              valiDateOptionalFormField(values[field])
            ) {
              formData.append(field, values[field]);
            }
          }
        });

        // If the "id" field is present, it means the form is for updating an existing user
        if (values.id) {
          // Append the "id" field for updating
          formData.append("id", values.id);

          // Use the "updateUser" mutation function for updating an existing user
          const updatedUser = await updateUser(formData).unwrap();

          const formatUpdatedUserData = parseUserData(updatedUser);

          setEditEmployeeData(formatUpdatedUserData);

          // Update user in local store
          dispatch(updateEmployeeInStore(updatedUser));

          userToastMessage = "updated";
        } else {
          const newData = await createUser(formData).unwrap();

          // Add user in local store
          dispatch(addEmployeeInStore(newData));
        }

        showSuccessToast(`User ${userToastMessage} successfully`);
      } catch (err) {
        // Error toast is automatically shown by baseQuery
      }
    };

    const deleteEmployee = async (employeeId) => {
      try {
        if (userInfo.id === employeeId) {
          showErrorToast("Current user cannot be deleted");
          return;
        }

        await deleteUser(employeeId).unwrap();

        dispatch(deleteEmployeeInStore(employeeId));

        showSuccessToast("User deleted successfully");

        refetch();
      } catch (err) {
        // Error toast is automatically shown by baseQuery
      }
    };

    return (
      <section className="section dashboard">
        {(isAllowedToCreate && !_ehphide) && (
          <ModalButton
            handleShow={handleShowAddEmployeeModal}
            target="addEmployeeModal"
            title="Add Employee"
          />
        )}
        <EmployeesViewManagement
          teamId={teamId}
          isAllowedToUpdate={isAllowedToUpdate}
          isAllowedToDelete={isAllowedToDelete}
          deleteEmployee={deleteEmployee}
          handleEditEmployee={handleEditEmployee}
        />
        {( isAllowedToCreate || isAllowedToUpdate && !_ehphide ) && (
          <Modal
            show={showAddEmployeeModal}
            onHide={handleCloseAddEmployeeModal}
            id="employeeModal"
            size="xl"
            scrollable={true}
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {editEmployeeData ? "Edit Details" : "Add Details"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-start">
              <EmployeeForm
                formId="employee_form"
                handleClose={handleCloseAddEmployeeModal}
                handleSubmit={handleSubmit}
                designationOptions={designationOptions}
                teamOptions={teamOptions}
                shiftOptions={shiftOptions}
                roleOptions={roleOptions}
                initialValues={editEmployeeData}
                isAllowedToUpdate={isAllowedToUpdate || isAllowedToCreate}
                isAllowedToDelete={isAllowedToDelete}
                deleteUserProfilePicture={deleteUserProfilePicture}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseAddEmployeeModal} disabled={isSubmitting}>
                Close
              </Button>
              <Button variant="primary" form="employee_form" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      animation="border"
                      size="sm"
                      role="status"
                      className="me-2"
                      style={{ width: "18px", height: "18px" }}
                    />
                    {editEmployeeData ? "Updating..." : "Adding..."}
                  </>
                ) : (
                  editEmployeeData ? "Save" : "Add Employee"
                )}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </section>
    );
  }
);

export default GeneralAccessManagement;
